import { useEffect, useMemo, useState, useCallback } from 'react';
import Map, { MapMarker, MarkerStyle } from '../components/Map';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ComboboxSingle } from '../components/ui/combobox';
import { Airport, Machine, PlanogramConfig, SKUAvailability } from '../types/map';
import { getMachines } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Filter, List as ListIcon, Map as MapIcon, ChevronLeft, ChevronRight, RotateCcw, Layers, X, ChevronDown, Calendar, Clock, MapPin, CheckSquare, CheckCircle2 } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { MAP_STYLES, MapStyle, DEFAULT_MAP_STYLE } from '../lib/mapConfig';
import { ReservationRequestModal } from '../components/ReservationRequestModal';
import { TenderRequestModal } from '../components/TenderRequestModal';
import { useToast } from '../components/ui/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import BeforeAfterSlider from '../components/BeforeAfterSlider';

type MachineWithAirport = {
  machine: Machine;
  airport: Airport;
};

const DEFAULT_MAP_CENTER: [number, number] = [54.0, -2.5];
const DEFAULT_MAP_ZOOM = 5.8;
const SELECTED_MAP_ZOOM = 18;


const venueTypeOptions = [
  { value: 'all', label: 'All venue types' },
  { value: 'Health & Fitness', label: 'Health & Fitness' },
  { value: 'Travel', label: 'Travel' },
  { value: 'Retail', label: 'Retail' },
  { value: 'Universities', label: 'Universities' },
];

const statusOptions = [
  { value: 'all', label: 'All statuses' },
  { value: 'Live/Available', label: 'Live/Available' },
  { value: 'Reserved', label: 'Reserved' },
  { value: 'Coming Soon', label: 'Coming Soon' },
];

// Availability type options will be fetched dynamically

export default function Home() {
  const { toast } = useToast();
  const [machinesByAirport, setMachinesByAirport] = useState<Record<string, Machine[]>>({});
  const [viewMode, setViewMode] = useState<'list' | 'map'>('map');
  const [searchTerm, setSearchTerm] = useState('');
  const [venueTypeFilter, setVenueTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [availabilityTypeFilter, setAvailabilityTypeFilter] = useState<string>('all');
  const [hasAvailableSkus, setHasAvailableSkus] = useState<string>('all');
  const [screensPerPage, setScreensPerPage] = useState(1000);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedMachines, setSelectedMachines] = useState<MachineWithAirport[]>([]);
  const [mapCenter, setMapCenter] = useState<[number, number]>(DEFAULT_MAP_CENTER);
  const [mapZoom, setMapZoom] = useState(DEFAULT_MAP_ZOOM);
  const [mapStyle, setMapStyle] = useState<MapStyle>(DEFAULT_MAP_STYLE);
  const [markerStyle, setMarkerStyle] = useState<MarkerStyle>('circle');
  const [loadingAirports, setLoadingAirports] = useState(true);
  const [loadingMachines, setLoadingMachines] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showFullDetails, setShowFullDetails] = useState(false);
  const [selectedMachineForDetails, setSelectedMachineForDetails] = useState<MachineWithAirport | null>(null);
  const [skuAvailability, setSkuAvailability] = useState<SKUAvailability | null>(null);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [expandedVenueTypes, setExpandedVenueTypes] = useState<Set<string>>(new Set());
  const [expandedSubcategories, setExpandedSubcategories] = useState<Set<string>>(new Set());
  const [hasInitializedExpansion, setHasInitializedExpansion] = useState(false);
  const [mapBounds, setMapBounds] = useState<{ north: number; south: number; east: number; west: number } | null>(null);
  const [availabilityTypeOptions, setAvailabilityTypeOptions] = useState<{ value: string; label: string }[]>([
    { value: 'all', label: 'All availability types' },
  ]);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [userFirstName, setUserFirstName] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userLoading, setUserLoading] = useState(true);

  const loadData = async () => {
    try {
      setLoadingMachines(true);
      
      // Load availability type options
      try {
        const availTypes = await fetch('/api/admin/field-options?fieldName=availabilityType', {
          credentials: 'include',
        });
        if (availTypes.ok) {
          const data = await availTypes.json();
          const options = [
            { value: 'all', label: 'All availability types' },
            ...data.map((opt: any) => ({ value: opt.value, label: opt.value })),
          ];
          setAvailabilityTypeOptions(options);
        }
      } catch (error) {
        console.error('Error loading availability types:', error);
      }
      
      // Load all machines (venue type based)
      const allMachines = await getMachines();
      
      // Group machines by venue type
      const grouped: Record<string, Machine[]> = {};
      allMachines.forEach((machine) => {
        const venueType = machine.venueType || 'Uncategorized';
        if (!grouped[venueType]) {
          grouped[venueType] = [];
        }
        grouped[venueType].push(machine);
      });
      
      setMachinesByAirport(grouped); // Reusing this state for venue types now
      setLoadError(null);
      
      // Keep venue types collapsed by default on page load
      // User can click to expand each venue type as needed
    } catch (error) {
      console.error('Failed to load data:', error);
      setLoadError('Unable to load machines.');
    } finally {
      setLoadingAirports(false);
      setLoadingMachines(false);
    }
  };

  useEffect(() => {
    loadData();
    // Load user info for welcome message
    setUserLoading(true);
    fetch('/auth/me', { credentials: 'include' })
      .then(res => {
        if (!res.ok) {
          console.error('Failed to fetch user info:', res.status);
          return null;
        }
        return res.json();
      })
      .then(data => {
        if (data) {
          console.log('User data from /auth/me:', data);
          if (data.firstName) {
            setUserFirstName(data.firstName);
          }
          if (data.email) {
            setUserEmail(data.email);
          }
        }
      })
      .catch(err => console.error('Error loading user info:', err))
      .finally(() => {
        setUserLoading(false);
      });
  }, []);

  const machinesWithAirport = useMemo<MachineWithAirport[]>(() => {
    // machinesByAirport now contains venue types as keys
    const allMachines: MachineWithAirport[] = [];
    
    Object.entries(machinesByAirport).forEach(([venueType, machines]) => {
      machines.forEach((machine) => {
        // Create a synthetic airport object using venue information
        const airport: Airport = {
          id: venueType, // Use venueType as ID for grouping
          code: machine.venueName?.substring(0, 3).toUpperCase() || venueType.substring(0, 3).toUpperCase(),
          name: machine.venueName || venueType,
          lat: machine.lat,
          lng: machine.lng,
          zoom: 15,
          isActive: true,
        };
        allMachines.push({ machine, airport });
      });
    });
    
    return allMachines;
  }, [machinesByAirport]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, venueTypeFilter, statusFilter, availabilityTypeFilter, hasAvailableSkus, screensPerPage]);

  const filteredMachines = useMemo<MachineWithAirport[]>(() => {
    const term = searchTerm.trim().toLowerCase();

    let result = machinesWithAirport;

    if (term) {
      result = result.filter(({ machine, airport }) => {
        return (
          machine.name.toLowerCase().includes(term) ||
          (machine.description && machine.description.toLowerCase().includes(term)) ||
          (machine.label && machine.label.toLowerCase().includes(term)) ||
          airport.name.toLowerCase().includes(term) ||
          airport.code.toLowerCase().includes(term)
        );
      });
    }

    if (venueTypeFilter !== 'all') {
      result = result.filter(({ machine }) => machine.venueType === venueTypeFilter);
    }

    if (statusFilter !== 'all') {
      result = result.filter(({ machine }) => machine.status === statusFilter);
    }

    if (availabilityTypeFilter !== 'all') {
      result = result.filter(({ machine }) => machine.availabilityType === availabilityTypeFilter);
    }

    if (hasAvailableSkus === 'yes') {
      result = result.filter(({ machine }) => machine.availableSkus && machine.availableSkus > 0);
    } else if (hasAvailableSkus === 'no') {
      result = result.filter(({ machine }) => !machine.availableSkus || machine.availableSkus === 0);
    }

    // Sort by availability (active first, then by name)
    const sorted = [...result];
    sorted.sort((a, b) => {
      const aActive = a.machine.isActive ? 1 : 0;
      const bActive = b.machine.isActive ? 1 : 0;
      if (bActive !== aActive) {
        return bActive - aActive;
      }
      return a.machine.name.localeCompare(b.machine.name);
    });

    return sorted;
  }, [machinesWithAirport, searchTerm, venueTypeFilter, statusFilter, availabilityTypeFilter, hasAvailableSkus]);

  useEffect(() => {
    // Remove any selected machines that no longer match filters
    const validIds = new Set(filteredMachines.map(m => m.machine.id));
    setSelectedMachines(prev => prev.filter(m => validIds.has(m.machine.id)));
  }, [filteredMachines]);

  const totalScreens = filteredMachines.length;
  const totalPages = Math.max(1, Math.ceil(totalScreens / screensPerPage));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  // Fetch SKU availability when viewing a tender machine
  useEffect(() => {
    if (selectedMachineForDetails?.machine.requiresTenderProcess && selectedMachineForDetails.machine.planogramConfig) {
      fetch(`/api/brand/reservations/machines/${selectedMachineForDetails.machine.id}/sku-availability`)
        .then(res => {
          if (res.ok) {
            return res.json();
          }
          return null;
        })
        .then(data => {
          if (data) setSkuAvailability(data);
        })
        .catch(err => {
          console.error('Error fetching SKU availability:', err);
        });
    } else {
      setSkuAvailability(null);
    }
  }, [selectedMachineForDetails]);

  const paginatedMachines = useMemo<MachineWithAirport[]>(() => {
    const start = (currentPage - 1) * screensPerPage;
    return filteredMachines.slice(start, start + screensPerPage);
  }, [filteredMachines, currentPage, screensPerPage]);

  const groupedPaginatedMachines = useMemo(() => {
    // Structure: VenueType -> Status -> VenueName -> Machines[]
    const groups = new globalThis.Map<string, globalThis.Map<string, globalThis.Map<string, MachineWithAirport[]>>>();
    
    paginatedMachines.forEach((entry) => {
      const venueType = entry.machine.venueType || 'Uncategorized';
      const status = entry.machine.status || 'Live/Available';
      const venueName = entry.machine.venueName || 'Other';
      
      if (!groups.has(venueType)) {
        groups.set(venueType, new globalThis.Map());
      }
      const venueMap = groups.get(venueType)!;
      
      if (!venueMap.has(status)) {
        venueMap.set(status, new globalThis.Map());
      }
      const statusMap = venueMap.get(status)!;
      
      if (!statusMap.has(venueName)) {
        statusMap.set(venueName, []);
      }
      statusMap.get(venueName)!.push(entry);
    });
    
    return groups;
  }, [paginatedMachines]);

  // Calculate visible machines based on map bounds
  const visibleMachines = useMemo(() => {
    if (!mapBounds) return filteredMachines;
    
    return filteredMachines.filter(({ machine }) => {
      return (
        machine.lat >= mapBounds.south &&
        machine.lat <= mapBounds.north &&
        machine.lng >= mapBounds.west &&
        machine.lng <= mapBounds.east
      );
    });
  }, [filteredMachines, mapBounds]);

  const mapMarkers: MapMarker[] = useMemo(() => {
    // Show filtered machines with automatic clustering
    const selectedIds = new Set(selectedMachines.map(m => m.machine.id));
    return filteredMachines.map(({ machine, airport }) => {
      const isSelected = selectedIds.has(machine.id);
      return {
        lat: machine.lat,
        lng: machine.lng,
        title: machine.label ? `${machine.name} (${machine.label})` : machine.name,
        color: machine.pinColor || undefined,
        onClick: () => {
          const entry = { machine, airport };
          handleToggleSelection(entry);
        },
      };
    });
  }, [filteredMachines, selectedMachines]);

  useEffect(() => {
    if (viewMode !== 'map') return;

    if (selectedMachines.length === 1) {
      setMapCenter([selectedMachines[0].machine.lat, selectedMachines[0].machine.lng]);
      setMapZoom(SELECTED_MAP_ZOOM);
    }
  }, [viewMode, selectedMachines]);

  // Expand venue types on initial load (but keep status sections collapsed)
  useEffect(() => {
    if (hasInitializedExpansion || groupedPaginatedMachines.size === 0) return;

    const venueTypes = Array.from(groupedPaginatedMachines.keys());
    const newExpandedVenueTypes = new Set(venueTypes);
    setExpandedVenueTypes(newExpandedVenueTypes);
    
    // Don't expand status sections or venue names by default
    setHasInitializedExpansion(true);
  }, [groupedPaginatedMachines, hasInitializedExpansion]);

  const startItem = totalScreens === 0 ? 0 : (currentPage - 1) * screensPerPage + 1;
  const endItem = totalScreens === 0 ? 0 : Math.min(currentPage * screensPerPage, totalScreens);

  // Calculate selection stats
  const selectionStats = useMemo(() => {
    if (selectedMachines.length === 0) {
      return { venueCount: 0, machineCount: 0, uniqueVenueTypes: new Set<string>() };
    }
    const uniqueVenueTypes = new Set(selectedMachines.map(m => m.machine.venueType).filter(Boolean));
    return {
      venueCount: uniqueVenueTypes.size,
      machineCount: selectedMachines.length,
      uniqueVenueTypes
    };
  }, [selectedMachines]);

  function handleToggleSelection(entry: MachineWithAirport) {
    setShowFullDetails(false); // Reset expanded state when switching machines
    setSelectedMachines(prev => {
      const isAlreadySelected = prev.some(m => m.machine.id === entry.machine.id);
      if (isAlreadySelected) {
        return prev.filter(m => m.machine.id !== entry.machine.id);
      } else {
        return [...prev, entry];
      }
    });
  }

  function handleClearSelection() {
    setSelectedMachines([]);
    setShowFullDetails(false);
  }

  function handleResetMap() {
    setSelectedMachines([]);
    setShowFullDetails(false);
    setMapCenter([...DEFAULT_MAP_CENTER]);
    setMapZoom(DEFAULT_MAP_ZOOM);
  }

  function handleSelectAllVisible() {
    setSelectedMachines(visibleMachines);
  }

  function handleViewInList() {
    setViewMode('list');
  }


  function toggleVenueType(venueType: string) {
    setExpandedVenueTypes(prev => {
      const next = new Set(prev);
      if (next.has(venueType)) {
        next.delete(venueType);
      } else {
        next.add(venueType);
      }
      return next;
    });
  }

  function toggleSubcategory(venueType: string, venueName: string) {
    const key = `${venueType}:${venueName}`;
    setExpandedSubcategories(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  function selectAllInVenueType(subcategoriesMap: globalThis.Map<string, MachineWithAirport[]>) {
    const allMachines: MachineWithAirport[] = [];
    subcategoriesMap.forEach(machines => {
      allMachines.push(...machines);
    });
    
    // Check if all are already selected
    const allSelected = allMachines.every(entry => 
      selectedMachines.some(m => m.machine.id === entry.machine.id)
    );
    
    if (allSelected) {
      // Deselect all
      setSelectedMachines(prev => 
        prev.filter(selected => !allMachines.some(m => m.machine.id === selected.machine.id))
      );
    } else {
      // Select all
      setSelectedMachines(prev => {
        const newSelections = allMachines.filter(entry => 
          !prev.some(m => m.machine.id === entry.machine.id)
        );
        return [...prev, ...newSelections];
      });
    }
  }

  function selectAllInSubcategory(machines: MachineWithAirport[]) {
    // Check if all are already selected
    const allSelected = machines.every(entry => 
      selectedMachines.some(m => m.machine.id === entry.machine.id)
    );
    
    if (allSelected) {
      // Deselect all
      setSelectedMachines(prev => 
        prev.filter(selected => !machines.some(m => m.machine.id === selected.machine.id))
      );
    } else {
      // Select all
      setSelectedMachines(prev => {
        const newSelections = machines.filter(entry => 
          !prev.some(m => m.machine.id === entry.machine.id)
        );
        return [...prev, ...newSelections];
      });
    }
  }

  function renderStatusPill(status: string) {
    let label = status;
    let colorClasses = '';
    let showDot = false;

    if (status === 'Live/Available') {
      label = 'Live';
      colorClasses = 'bg-emerald-100 text-emerald-700 border border-emerald-200';
      showDot = true;
    } else if (status === 'Reserved') {
      label = 'Reserved';
      colorClasses = 'bg-amber-100 text-amber-700 border border-amber-200';
    } else if (status === 'Coming Soon') {
      label = 'Coming Soon';
      colorClasses = 'bg-blue-100 text-blue-700 border border-blue-200';
    } else {
      colorClasses = 'bg-slate-100 text-slate-700 border border-slate-200';
    }

    return (
      <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${colorClasses}`}>
        {showDot && <span className="w-2 h-2 rounded-full bg-emerald-600"></span>}
        {label}
      </span>
    );
  }

  return (
    <div className="h-full">
      <div className={`px-2 md:px-4 ${selectedMachines.length > 0 ? 'pb-24' : 'pb-12'}`}>
        <div className="w-full">
          <div className="flex flex-col gap-4 md:gap-6 pt-3 md:pt-6 pb-2 md:pb-4">
            <div className="flex flex-col gap-3 md:gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-slate-900">
                  {userLoading ? (
                    <span>Hey, <span className="font-extrabold" style={{ color: 'hsl(var(--brand-primary))' }}>...</span></span>
                  ) : (
                    <span>
                      Hey, <span className="font-extrabold" style={{ color: 'hsl(var(--brand-primary))' }}>{userFirstName 
                        ? userFirstName.charAt(0).toUpperCase() + userFirstName.slice(1).toLowerCase()
                        : (userEmail ? userEmail.split('@')[0].charAt(0).toUpperCase() + userEmail.split('@')[0].slice(1).toLowerCase() : 'there')}</span>
                    </span>
                  )}
                </h1>
                <p className="text-xs md:text-sm text-slate-500">
                  Browse and select machines across all venue types.
                </p>
              </div>
              <div className="inline-flex items-center rounded-full border border-slate-200 bg-white p-1 shadow-sm w-fit">
                <button
                  type="button"
                  className={`flex items-center gap-1.5 md:gap-2 rounded-full px-3 md:px-4 py-2.5 md:py-2 text-xs md:text-sm font-semibold transition-colors min-h-[44px] md:min-h-0 ${
                    viewMode === 'list'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  onClick={() => setViewMode('list')}
                >
                  <ListIcon className="h-4 w-4" />
                  <span>List</span>
                </button>
                <button
                  type="button"
                  className={`flex items-center gap-1.5 md:gap-2 rounded-full px-3 md:px-4 py-2.5 md:py-2 text-xs md:text-sm font-semibold transition-colors min-h-[44px] md:min-h-0 ${
                    viewMode === 'map'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  onClick={() => setViewMode('map')}
                >
                  <MapIcon className="h-4 w-4" />
                  <span>Map</span>
                </button>
              </div>
            </div>

          </div>

          {loadError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
              {loadError}
            </div>
          )}

          {viewMode === 'list' && (
            <div className="space-y-6">
              {/* Filters Button */}
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={() => setFiltersExpanded(!filtersExpanded)}
                  className="flex items-center gap-2 min-h-[44px] px-4 py-2.5 md:px-3 md:py-2"
                >
                  <Filter className="h-4 w-4" />
                  <span className="text-sm md:text-base">Filters</span>
                </Button>
              </div>

              {/* Filters Section - Collapsible */}
              {filtersExpanded && (
                <div className="bg-white rounded-xl border border-slate-200 p-3 md:p-5 shadow-sm space-y-3 md:space-y-4 relative z-[1000]">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 md:h-5 md:w-5 text-slate-600" />
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">Filters</h2>
              </div>
              
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <Input
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search..."
                    className="w-full min-h-[44px] text-base md:text-sm"
                  />
                </div>
                
                    <div className="relative z-[2000]">
                  <ComboboxSingle
                    options={venueTypeOptions}
                    value={venueTypeFilter}
                    onValueChange={setVenueTypeFilter}
                    placeholder="All venue types"
                    searchPlaceholder="Search venue types..."
                    emptyMessage="No venue types found."
                  />
                </div>

                    <div className="relative z-[2000]">
                  <ComboboxSingle
                    options={statusOptions}
                    value={statusFilter}
                    onValueChange={setStatusFilter}
                    placeholder="All statuses"
                    searchPlaceholder="Search statuses..."
                    emptyMessage="No statuses found."
                  />
                </div>

                    <div className="relative z-[2000]">
                  <ComboboxSingle
                    options={availabilityTypeOptions}
                    value={availabilityTypeFilter}
                    onValueChange={setAvailabilityTypeFilter}
                    placeholder="All availability types"
                    searchPlaceholder="Search availability types..."
                    emptyMessage="No availability types found."
                  />
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">Has Available SKUs:</span>
                      <div className="relative z-[2000]">
                  <ComboboxSingle
                    options={[
                      { value: 'all', label: 'All' },
                      { value: 'yes', label: 'Yes' },
                      { value: 'no', label: 'No' }
                    ]}
                    value={hasAvailableSkus}
                    onValueChange={setHasAvailableSkus}
                    placeholder="All"
                    className="w-[120px]"
                  />
                </div>
              </div>
            </div>
            </div>
          )}

              {loadingAirports || loadingMachines ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((group) => (
                    <div key={group} className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                      <div className="h-4 w-48 animate-pulse rounded bg-slate-200" />
                      {[1, 2].map((row) => (
                        <div key={row} className="flex flex-col gap-3 rounded-lg border border-slate-100 bg-slate-50 p-4 md:flex-row md:items-center md:justify-between">
                          <div className="space-y-2">
                            <div className="h-4 w-40 animate-pulse rounded bg-slate-200" />
                            <div className="h-3 w-32 animate-pulse rounded bg-slate-200" />
                          </div>
                          <div className="grid w-full gap-3 md:grid-cols-4 md:gap-4">
                            {[1, 2, 3, 4].map((item) => (
                              <div key={item} className="h-3 animate-pulse rounded bg-slate-200" />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-6">
                  {Array.from(groupedPaginatedMachines.entries()).map(([venueType, statusMap]) => {
                    if (!statusMap || statusMap.size === 0) return null;

                    // Calculate total machines and available count across all statuses and subcategories
                    let totalMachines = 0;
                    let totalAvailable = 0;
                    statusMap.forEach((subcategoriesMap) => {
                      subcategoriesMap.forEach((machines) => {
                        totalMachines += machines.length;
                        totalAvailable += machines.filter(entry => entry.machine.isActive).length;
                      });
                    });

                    const isVenueTypeExpanded = expandedVenueTypes.has(venueType);

                    return (
                      <div key={venueType} className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                        {/* Venue Type Header - Collapsible */}
                        <div className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                          <button
                            onClick={() => toggleVenueType(venueType)}
                            className="flex items-center gap-3 flex-1"
                          >
                            <ChevronDown 
                              className={`h-5 w-5 text-slate-400 transition-transform ${isVenueTypeExpanded ? 'transform rotate-0' : 'transform -rotate-90'}`}
                            />
                            <div className="text-left">
                              <h2 className="text-lg font-semibold text-slate-900">{venueType}</h2>
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                                {totalMachines} {totalMachines === 1 ? 'Machine' : 'Machines'} • {totalAvailable} Available
                              </p>
                            </div>
                          </button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Collect all machines from all statuses
                              const allMachines: MachineWithAirport[] = [];
                              statusMap.forEach((subcategoriesMap) => {
                                subcategoriesMap.forEach((machines) => {
                                  allMachines.push(...machines);
                                });
                              });
                              const tempMap = new globalThis.Map<string, MachineWithAirport[]>();
                              tempMap.set('all', allMachines);
                              selectAllInVenueType(tempMap);
                            }}
                            className="text-xs"
                          >
                            Select All
                          </Button>
                        </div>

                        {/* Status Sections - Only shown when venue type is expanded */}
                        {isVenueTypeExpanded && (
                          <div className="border-t border-slate-100">
                            {Array.from(statusMap.entries()).map(([status, subcategoriesMap]) => {
                              // Calculate totals for this status
                              let statusTotalMachines = 0;
                              let statusTotalAvailable = 0;
                              subcategoriesMap.forEach((machines) => {
                                statusTotalMachines += machines.length;
                                statusTotalAvailable += machines.filter(entry => entry.machine.isActive).length;
                              });

                              const statusKey = `${venueType}:${status}`;
                              const isStatusExpanded = expandedSubcategories.has(statusKey);

                              return (
                                <div key={statusKey} className="border-b border-slate-100 last:border-b-0">
                                  {/* Status Header - Collapsible */}
                                  <div className="p-3 pl-8 flex items-center justify-between hover:bg-slate-50 transition-colors bg-blue-50/30">
                                    <button
                                      onClick={() => {
                                        const statusKey = `${venueType}:${status}`;
                                        setExpandedSubcategories(prev => {
                                          const next = new Set(prev);
                                          if (next.has(statusKey)) {
                                            next.delete(statusKey);
                                          } else {
                                            next.add(statusKey);
                                          }
                                          return next;
                                        });
                                      }}
                                      className="flex items-center gap-2 flex-1"
                                    >
                                      <ChevronDown 
                                        className={`h-4 w-4 text-slate-400 transition-transform ${isStatusExpanded ? 'transform rotate-0' : 'transform -rotate-90'}`}
                                      />
                                      <div className="text-left flex items-center gap-2">
                                        {renderStatusPill(status)}
                                        <div>
                                          <h3 className="text-sm font-semibold text-slate-700">{status}</h3>
                                          <p className="text-xs text-slate-500">
                                            {statusTotalMachines} {statusTotalMachines === 1 ? 'Machine' : 'Machines'} • {statusTotalAvailable} Available
                                          </p>
                                        </div>
                                      </div>
                                    </button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // Collect all machines from this status
                                        const statusMachines: MachineWithAirport[] = [];
                                        subcategoriesMap.forEach((machines) => {
                                          statusMachines.push(...machines);
                                        });
                                        selectAllInSubcategory(statusMachines);
                                      }}
                                      className="text-xs mr-2"
                                    >
                                      Select All
                                    </Button>
                                  </div>

                                  {/* Venue Names - Only shown when status is expanded */}
                                  {isStatusExpanded && (
                                    <div className="border-t border-slate-100 bg-white">
                                      {Array.from(subcategoriesMap.entries()).map(([venueName, machines]) => {
                                        const subcategoryKey = `${venueType}:${status}:${venueName}`;
                                        const isSubcategoryExpanded = expandedSubcategories.has(subcategoryKey);
                                        const availableCount = machines.filter(entry => entry.machine.isActive).length;

                                        return (
                                          <div key={subcategoryKey} className="border-b border-slate-100 last:border-b-0">
                                            {/* Venue Name Header - Collapsible */}
                                            <div className="p-3 pl-16 flex items-center justify-between hover:bg-slate-50 transition-colors bg-slate-50/50">
                                              <button
                                                onClick={() => toggleSubcategory(venueType, `${status}:${venueName}`)}
                                                className="flex items-center gap-2 flex-1"
                                              >
                                                <ChevronDown 
                                                  className={`h-4 w-4 text-slate-400 transition-transform ${isSubcategoryExpanded ? 'transform rotate-0' : 'transform -rotate-90'}`}
                                                />
                                                <div className="text-left">
                                                  <h3 className="text-sm font-semibold text-slate-700">{venueName}</h3>
                                                  <p className="text-xs text-slate-500">
                                                    {machines.length} {machines.length === 1 ? 'Machine' : 'Machines'} • {availableCount} Available
                                                  </p>
                                                </div>
                                              </button>
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  selectAllInSubcategory(machines);
                                                }}
                                                className="text-xs mr-2"
                                              >
                                                Select All
                                              </Button>
                                            </div>

                                            {/* Machine List - Only shown when venue name is expanded */}
                                            {isSubcategoryExpanded && (
                                              <div className="divide-y divide-slate-100 bg-white">
                                                {machines.map((entry: MachineWithAirport) => {
                            const { machine, airport } = entry;
                            const isSelected = selectedMachines.some(m => m.machine.id === machine.id);
                            const venueType = machine.venueType || '—';
                            const location = machine.location || '—';
                            const availabilityType = machine.availabilityType || '—';
                            
                            // Determine display status based on reservation info
                            // Only show pending/reserved for the current user's brand
                            // For planogram machines, only show reserved if planogram is fully filled
                            let displayStatus: string = machine.status || 'Live/Available';
                            if (machine.reservationInfo?.userReservationStatus === 'pending') {
                              displayStatus = 'Pending';
                            } else if (machine.reservationInfo?.userReservationStatus === 'approved') {
                              // For planogram machines, only show reserved if fully filled
                              if (machine.requiresTenderProcess && machine.reservationInfo?.isPlanogramFullyReserved) {
                                displayStatus = 'Reserved';
                              } else if (!machine.requiresTenderProcess) {
                              displayStatus = 'Reserved';
                              }
                              // If planogram machine but not fully filled, keep as Available
                            }

                            return (
                              <div
                                key={machine.id}
                                onClick={() => handleToggleSelection(entry)}
                                className={`relative flex flex-col gap-4 p-4 transition-colors md:flex-row md:items-center md:justify-between cursor-pointer ${
                                  isSelected ? 'bg-blue-50' : 'hover:bg-slate-50'
                                }`}
                              >
                                {/* Status Badge - Top Right Corner */}
                                <div className="absolute top-3 right-3 flex items-center gap-2 z-10">
                                  <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${
                                    displayStatus === 'Live/Available' || displayStatus === 'Live' ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' :
                                    displayStatus === 'Pending' ? 'bg-yellow-100 text-yellow-700 border border-yellow-200' :
                                    displayStatus === 'Reserved' ? 'bg-amber-100 text-amber-700 border border-amber-200' :
                                    'bg-blue-100 text-blue-700 border border-blue-200'
                                  }`}>
                                    {displayStatus === 'Live/Available' && (
                                      <span className="w-2 h-2 rounded-full bg-emerald-600"></span>
                                    )}
                                    {displayStatus === 'Pending' && (
                                      <Clock className="w-3 h-3" />
                                    )}
                                    {displayStatus === 'Live/Available' ? 'Live' : displayStatus}
                                  </span>
                                  {machine.availableSkus && machine.availableSkus > 0 && (
                                    <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                                      {machine.availableSkus} SKU{machine.availableSkus > 1 ? 's' : ''}
                                    </span>
                                  )}
                                  {machine.requiresTenderProcess && (
                                    <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold bg-purple-100 text-purple-700 border border-purple-200">
                                      New Location
                                    </span>
                                  )}
                                </div>

                                <div className="flex flex-1 flex-col gap-3 md:flex-row md:items-center">
                                  <div className="flex items-center gap-3">
                                    <div onClick={(e) => e.stopPropagation()}>
                                      <Checkbox
                                        checked={isSelected}
                                        onCheckedChange={() => handleToggleSelection(entry)}
                                        className="h-5 w-5"
                                      />
                                    </div>
                                    {machine.MachineMedia && machine.MachineMedia.length > 0 ? (
                                      <div className="relative h-24 w-32 flex-shrink-0 rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
                                        <img
                                          src={machine.MachineMedia.find(m => m.kind === 'area')?.url || machine.MachineMedia[0].url}
                                          alt={machine.name}
                                          className="w-full h-full object-cover"
                                          onError={(e) => {
                                            (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23f1f5f9" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" font-family="Arial" font-size="14" fill="%2394a3b8" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                                          }}
                                        />
                                        <div className="absolute top-1 left-1 bg-blue-600 text-white text-xs font-semibold px-2 py-0.5 rounded">
                                          {airport.code}
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="flex h-24 w-32 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100 text-lg font-semibold text-blue-700 border border-blue-200">
                                        {airport.code}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-1 space-y-2 pr-24 md:pr-32">
                                    <div>
                                      {machine.label && (
                                        <div className="mb-2">
                                          <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 border border-blue-200">
                                            {machine.label}
                                          </span>
                                        </div>
                                      )}
                                      <h3 className="text-base font-semibold text-slate-900">
                                        {machine.name}
                                      </h3>
                                      {machine.description && (
                                        <p className="text-sm text-slate-500 line-clamp-1">
                                          {machine.description}
                                        </p>
                                      )}
                                    </div>
                                    <div className="grid gap-4 text-sm text-slate-600 sm:grid-cols-3">
                                      <div>
                                        <p className="text-xs uppercase text-slate-400">Venue Type</p>
                                        <p className="font-medium">{venueType}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs uppercase text-slate-400">Location</p>
                                        <p className="font-medium">{location}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs uppercase text-slate-400">Availability Type</p>
                                        <p className="font-medium">{availabilityType}</p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex flex-row items-center gap-2">
                                  <Button
                                    variant="outline"
                                    className="rounded-full px-6 py-2 text-sm font-semibold"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedMachineForDetails(entry);
                                    }}
                                  >
                                    View
                                  </Button>
                                  <Button
                                    variant={isSelected ? 'default' : 'outline'}
                                    className="rounded-full px-6 py-2 text-sm font-semibold"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleToggleSelection(entry);
                                    }}
                                  >
                                    {isSelected ? 'Selected' : 'Select'}
                                  </Button>
                                </div>
                              </div>
                            );
                                                })}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {totalScreens === 0 && !loadingMachines && (
                    <div className="rounded-xl border border-slate-200 bg-white p-12 text-center shadow-sm">
                      <h3 className="text-lg font-semibold text-slate-900">No screens found</h3>
                      <p className="mt-2 text-sm text-slate-500">
                        Adjust your filters or search criteria to see more results.
                      </p>
                    </div>
                  )}

                  {/* Pagination Controls */}
                  {totalScreens > 0 && (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-slate-200">
                      <div className="text-sm text-slate-500">
                        Showing {startItem}-{endItem} of {totalScreens} machines
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500 whitespace-nowrap">Per page:</span>
                          <Select value={String(screensPerPage)} onValueChange={(value) => {
                            setScreensPerPage(Number(value));
                            setCurrentPage(1);
                          }}>
                            <SelectTrigger className="w-[100px] h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="25">25</SelectItem>
                              <SelectItem value="50">50</SelectItem>
                              <SelectItem value="100">100</SelectItem>
                              <SelectItem value="250">250</SelectItem>
                              <SelectItem value="500">500</SelectItem>
                              <SelectItem value="1000">1000</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            className="rounded-full border-slate-200 min-h-[44px] min-w-[44px] h-10 w-10 md:h-8 md:w-8"
                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <span className="text-sm font-medium text-slate-600 min-w-[80px] text-center">
                            Page {currentPage} of {totalPages}
                          </span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="rounded-full border-slate-200 min-h-[44px] min-w-[44px] h-10 w-10 md:h-8 md:w-8"
                            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {viewMode === 'map' && (
            <div className="mt-6 space-y-4">
              {/* Filters Button */}
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={() => setFiltersExpanded(!filtersExpanded)}
                  className="flex items-center gap-2 min-h-[44px] px-4 py-2.5 md:px-3 md:py-2"
                >
                  <Filter className="h-4 w-4" />
                  <span className="text-sm md:text-base">Filters</span>
                </Button>
              </div>

              {/* Filters Section - Collapsible */}
              {filtersExpanded && (
                <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-4 relative z-[1000]">
                  <div className="flex items-center gap-2">
                    <Filter className="h-5 w-5 text-slate-600" />
                    <h2 className="text-xl font-semibold text-slate-900">Filters</h2>
                  </div>
                  
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                      <Input
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        placeholder="Search..."
                        className="w-full"
                      />
                    </div>
                    
                    <div className="relative z-[2000]">
                      <ComboboxSingle
                        options={venueTypeOptions}
                        value={venueTypeFilter}
                        onValueChange={setVenueTypeFilter}
                        placeholder="All venue types"
                        searchPlaceholder="Search venue types..."
                        emptyMessage="No venue types found."
                      />
                    </div>

                    <div className="relative z-[2000]">
                      <ComboboxSingle
                        options={statusOptions}
                        value={statusFilter}
                        onValueChange={setStatusFilter}
                        placeholder="All statuses"
                        searchPlaceholder="Search statuses..."
                        emptyMessage="No statuses found."
                      />
                    </div>

                    <div className="relative z-[2000]">
                      <ComboboxSingle
                        options={availabilityTypeOptions}
                        value={availabilityTypeFilter}
                        onValueChange={setAvailabilityTypeFilter}
                        placeholder="All availability types"
                        searchPlaceholder="Search availability types..."
                        emptyMessage="No availability types found."
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-500">Has Available SKUs:</span>
                      <div className="relative z-[2000]">
                        <ComboboxSingle
                          options={[
                            { value: 'all', label: 'All' },
                            { value: 'yes', label: 'Yes' },
                            { value: 'no', label: 'No' }
                          ]}
                          value={hasAvailableSkus}
                          onValueChange={setHasAvailableSkus}
                          placeholder="All"
                          className="w-[120px]"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="relative rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" style={{ height: '70vh' }}>
                <Map 
                  center={mapCenter} 
                  zoom={mapZoom} 
                  markers={mapMarkers} 
                  mapStyle={mapStyle}
                  markerStyle={markerStyle}
                  onBoundsChange={setMapBounds}
                />
                
                
                {/* Map Controls */}
                <div className="absolute bottom-4 left-4 z-[1000] flex gap-3">
                  {/* Map Style Picker */}
                  <Select value={mapStyle} onValueChange={(value: MapStyle) => setMapStyle(value)}>
                    <SelectTrigger className="w-[60px] h-[60px] md:w-[75px] md:h-[75px] rounded-xl border-0 bg-black/60 backdrop-blur-sm text-white shadow-lg hover:bg-black/70 transition-all p-2 flex flex-col items-center justify-center gap-1">
                      <Layers className="w-5 h-5 md:w-6 md:h-6" />
                      <span className="text-[10px] md:text-xs font-medium">Layers</span>
                    </SelectTrigger>
                    <SelectContent className="z-[2000] w-[200px] rounded-xl border-0 shadow-xl bg-white p-1">
                      {Object.entries(MAP_STYLES).map(([key, config]) => (
                        <SelectItem 
                          key={key} 
                          value={key}
                          className="rounded-lg pl-8 pr-3 py-2 cursor-pointer hover:bg-slate-100 focus:bg-slate-100 relative"
                        >
                          <div className="flex flex-col">
                            <span className="font-medium text-slate-900">{config.name}</span>
                            <span className="text-xs text-slate-500">{config.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {/* Marker Style Picker */}
                  <Select value={markerStyle} onValueChange={(value: MarkerStyle) => setMarkerStyle(value)}>
                    <SelectTrigger className="w-[60px] h-[60px] md:w-[75px] md:h-[75px] rounded-xl border-0 bg-black/60 backdrop-blur-sm text-white shadow-lg hover:bg-black/70 transition-all p-2 flex flex-col items-center justify-center gap-1">
                      <MapPin className="w-5 h-5 md:w-6 md:h-6" />
                      <span className="text-[10px] md:text-xs font-medium">Markers</span>
                    </SelectTrigger>
                    <SelectContent className="z-[2000] w-[220px] rounded-xl border-0 shadow-xl bg-white p-1">
                      <SelectItem value="circle" className="rounded-lg pl-8 pr-3 py-2 cursor-pointer hover:bg-slate-100 focus:bg-slate-100 relative">
                        <div className="flex flex-col">
                          <span className="font-medium text-slate-900">Simple Circle</span>
                          <span className="text-xs text-slate-500">Solid colored dots</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="circle-border" className="rounded-lg pl-8 pr-3 py-2 cursor-pointer hover:bg-slate-100 focus:bg-slate-100 relative">
                        <div className="flex flex-col">
                          <span className="font-medium text-slate-900">Circle with Border</span>
                          <span className="text-xs text-slate-500">Clean outlined circles</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="rounded-square" className="rounded-lg pl-8 pr-3 py-2 cursor-pointer hover:bg-slate-100 focus:bg-slate-100 relative">
                        <div className="flex flex-col">
                          <span className="font-medium text-slate-900">Rounded Square</span>
                          <span className="text-xs text-slate-500">Modern square markers</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="dot-ring" className="rounded-lg pl-8 pr-3 py-2 cursor-pointer hover:bg-slate-100 focus:bg-slate-100 relative">
                        <div className="flex flex-col">
                          <span className="font-medium text-slate-900">Dot with Ring</span>
                          <span className="text-xs text-slate-500">Small dot in ring</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="pin-no-shadow" className="rounded-lg pl-8 pr-3 py-2 cursor-pointer hover:bg-slate-100 focus:bg-slate-100 relative">
                        <div className="flex flex-col">
                          <span className="font-medium text-slate-900">Pin (No Shadow)</span>
                          <span className="text-xs text-slate-500">Classic pin without shadow</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="pin" className="rounded-lg pl-8 pr-3 py-2 cursor-pointer hover:bg-slate-100 focus:bg-slate-100 relative">
                        <div className="flex flex-col">
                          <span className="font-medium text-slate-900">Pin with Shadow</span>
                          <span className="text-xs text-slate-500">Classic pin with shadow</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Reset Map Button and Machine Count */}
                <div className="absolute top-2 right-2 md:top-4 md:right-4 z-[1000] flex flex-col gap-2 md:gap-3">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleResetMap}
                    className="shadow-lg min-h-[44px] px-3 py-2.5 md:px-4 md:py-2 text-sm md:text-base"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">Reset Map</span>
                    <span className="sm:hidden">Reset</span>
                  </Button>
                  
                  {/* Machine Count Badge */}
                  <div className="bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border border-slate-200 px-3 py-2 md:px-4 md:py-3">
                    <div className="flex flex-col gap-2 md:gap-3">
                      <div className="flex items-center gap-2">
                        <MapIcon className="h-4 w-4 md:h-5 md:w-5 text-blue-600" />
                        <div>
                          <div className="text-xl md:text-2xl font-bold text-slate-900">{visibleMachines.length}</div>
                          <div className="text-xs text-slate-500 font-medium">
                            {visibleMachines.length === 1 ? 'Machine' : 'Machines'} visible
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex flex-col gap-2 pt-2 border-t border-slate-200">
                        <Button
                          variant="default"
                          size="sm"
                          onClick={handleSelectAllVisible}
                          disabled={visibleMachines.length === 0}
                          className="min-h-[44px] text-xs md:text-sm w-full py-2.5 md:py-2"
                        >
                          Select All ({visibleMachines.length})
                        </Button>
                        {selectedMachines.length > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleViewInList}
                            className="min-h-[44px] text-xs md:text-sm w-full py-2.5 md:py-2"
                          >
                            <ListIcon className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                            View in List ({selectedMachines.length})
                          </Button>
                        )}
                      </div>

                      {(venueTypeFilter !== 'all' || statusFilter !== 'all' || availabilityTypeFilter !== 'all' || searchTerm) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setVenueTypeFilter('all');
                            setStatusFilter('all');
                            setAvailabilityTypeFilter('all');
                            setSearchTerm('');
                            setHasAvailableSkus('all');
                          }}
                          className="min-h-[44px] px-3 py-2.5 md:px-2 md:py-1.5 text-xs md:text-sm text-slate-600 hover:text-slate-900 w-full"
                        >
                          <X className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                          Clear Filters
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {selectedMachines.length === 1 && (
                  <div className="absolute left-20 top-4 bottom-4 z-[1000] w-[420px] overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl">
                    {/* Header */}
                    <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
                      <div className="flex-1 min-w-0 pr-4">
                        {selectedMachines[0].machine.label && (
                          <div className="flex items-center gap-2 mb-2">
                            <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 border border-blue-200">
                              {selectedMachines[0].machine.label}
                            </span>
                          </div>
                        )}
                        <h2 className="text-xl font-bold text-slate-900">
                          {selectedMachines[0].machine.name}
                        </h2>
                      </div>
                      <button
                        onClick={handleClearSelection}
                        className="rounded-full p-2 hover:bg-slate-100 transition-colors flex-shrink-0"
                      >
                        <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    {/* Image */}
                    {selectedMachines[0].machine.MachineMedia && selectedMachines[0].machine.MachineMedia.length > 0 && (
                      <div className="relative w-full h-64 bg-slate-100">
                        <img
                          src={selectedMachines[0].machine.MachineMedia.find(m => m.kind === 'area')?.url || selectedMachines[0].machine.MachineMedia[0].url}
                          alt={selectedMachines[0].machine.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23f1f5f9" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" font-family="Arial" font-size="14" fill="%2394a3b8" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                          }}
                        />
                        <div className="absolute top-3 right-3">
                          {renderStatusPill(selectedMachines[0].machine.status || 'Live/Available')}
                        </div>
                      </div>
                    )}

                    {/* Content */}
                    <div className="px-6 py-5 space-y-5">
                      {/* Description */}
                      {selectedMachines[0].machine.description && (
                        <div>
                          <p className="text-base text-slate-600">
                            {selectedMachines[0].machine.description}
                          </p>
                        </div>
                      )}

                      {/* Basic Info - Always Visible */}
                      <div className="grid grid-cols-2 gap-5">
                        <div>
                          <h3 className="text-sm font-semibold text-slate-500 uppercase mb-1">Venue Type</h3>
                          <p className="text-base text-slate-900">
                            {selectedMachines[0].machine.venueType || '—'}
                          </p>
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-slate-500 uppercase mb-1">Location</h3>
                          <p className="text-base text-slate-900">
                            {selectedMachines[0].machine.location || '—'}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-5">
                        <div>
                          <h3 className="text-sm font-semibold text-slate-500 uppercase mb-1">Machine Type</h3>
                          <p className="text-base text-slate-900">
                            {selectedMachines[0].machine.machineType || '—'}
                          </p>
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-slate-500 uppercase mb-1">Resolution</h3>
                          <p className="text-base text-slate-900">
                            {selectedMachines[0].machine.resolution || '—'}
                          </p>
                        </div>
                      </div>

                      {/* View Full Details Button */}
                      {!showFullDetails && (
                        <div className="pt-2">
                          <Button
                            variant="outline"
                            onClick={() => setShowFullDetails(true)}
                            className="w-full"
                          >
                            View Full Details
                          </Button>
                        </div>
                      )}

                      {/* Extended Details - Shown when expanded */}
                      {showFullDetails && (
                        <div className="space-y-5 pt-4 border-t border-slate-200">
                          <div className="grid grid-cols-2 gap-5">
                            <div>
                              <h3 className="text-sm font-semibold text-slate-500 uppercase mb-1">Status</h3>
                              <p className="text-base text-slate-900">
                                {selectedMachines[0].machine.status || '—'}
                              </p>
                            </div>
                            <div>
                              <h3 className="text-sm font-semibold text-slate-500 uppercase mb-1">Availability Type</h3>
                              <p className="text-base text-slate-900">
                                {selectedMachines[0].machine.availabilityType || '—'}
                              </p>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-5">
                            <div>
                              <h3 className="text-sm font-semibold text-slate-500 uppercase mb-1">Available SKUs</h3>
                              <p className="text-base text-slate-900">
                                {selectedMachines[0].machine.availableSkus !== null && selectedMachines[0].machine.availableSkus !== undefined ? selectedMachines[0].machine.availableSkus : '—'}
                              </p>
                            </div>
                            <div>
                              <h3 className="text-sm font-semibold text-slate-500 uppercase mb-1">Capacity</h3>
                              <p className="text-base text-slate-900">
                                {selectedMachines[0].machine.capacity || '—'}
                              </p>
                            </div>
                          </div>

                          {/* Screen Specifications */}
                          <div>
                            <h3 className="text-lg font-semibold text-slate-900 mb-3">Screen Specifications</h3>
                            <div className="grid grid-cols-2 gap-5">
                              <div>
                                <h3 className="text-sm font-semibold text-slate-500 uppercase mb-1">Format</h3>
                                <p className="text-base text-slate-900">
                                  {selectedMachines[0].machine.format || '—'}
                                </p>
                              </div>
                              <div>
                                <h3 className="text-sm font-semibold text-slate-500 uppercase mb-1">Screen Size</h3>
                                <p className="text-base text-slate-900">
                                  {selectedMachines[0].machine.screenSize || '—'}
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-5">
                            <div>
                              <h3 className="text-sm font-semibold text-slate-500 uppercase mb-1">Playback Format</h3>
                              <p className="text-base text-slate-900">
                                {selectedMachines[0].machine.playbackFormat || '—'}
                              </p>
                            </div>
                            <div>
                              <h3 className="text-sm font-semibold text-slate-500 uppercase mb-1">Duration</h3>
                              <p className="text-base text-slate-900">
                                {selectedMachines[0].machine.duration || '—'}
                              </p>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-5">
                            <div>
                              <h3 className="text-sm font-semibold text-slate-500 uppercase mb-1">Orientation</h3>
                              <p className="text-base text-slate-900">
                                {selectedMachines[0].machine.orientation || '—'}
                              </p>
                            </div>
                            <div>
                              <h3 className="text-sm font-semibold text-slate-500 uppercase mb-1">Aspect Ratio</h3>
                              <p className="text-base text-slate-900">
                                {selectedMachines[0].machine.aspectRatio || '—'}
                              </p>
                            </div>
                          </div>

                          {/* Address */}
                          {(selectedMachines[0].machine.addressLine1 || selectedMachines[0].machine.city || selectedMachines[0].machine.postCode) && (
                            <div>
                              <h3 className="text-lg font-semibold text-slate-900 mb-3">Address</h3>
                              <div className="space-y-1 text-base text-slate-900">
                                {selectedMachines[0].machine.addressLine1 && <p>{selectedMachines[0].machine.addressLine1}</p>}
                                {selectedMachines[0].machine.addressLine2 && <p>{selectedMachines[0].machine.addressLine2}</p>}
                                <p>
                                  {[selectedMachines[0].machine.city, selectedMachines[0].machine.county, selectedMachines[0].machine.postCode]
                                    .filter(Boolean)
                                    .join(', ')}
                                </p>
                              </div>
                            </div>
                          )}

                          <div className="pt-2">
                            <Button
                              variant="ghost"
                              onClick={() => setShowFullDetails(false)}
                              className="w-full"
                            >
                              Hide Details
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Footer Buttons */}
                      <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4">
                      <Button
                        variant="ghost"
                        onClick={handleClearSelection}
                        className="text-base"
                      >
                        Close
                      </Button>
                      <Button
                        onClick={() => setViewMode('list')}
                        className="bg-blue-600 hover:bg-blue-700 text-white text-base px-6"
                      >
                        View in List
                      </Button>
                    </div>
                  </div>
                )}

                {selectedMachines.length > 1 && (
                  <div className="absolute left-16 top-6 z-[1000] w-[400px] max-h-[calc(100vh-100px)] overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl">
                    <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
                      <h2 className="text-lg font-bold text-slate-900">
                        {selectedMachines.length} Screens Selected
                      </h2>
                      <button
                        onClick={handleClearSelection}
                        className="rounded-full p-2 hover:bg-slate-100 transition-colors"
                      >
                        <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="px-4 py-3 space-y-2 max-h-[400px] overflow-y-auto">
                      {selectedMachines.map(({ machine, airport }) => (
                        <div key={machine.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-blue-100 text-sm font-semibold text-blue-700">
                              {airport.code}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm text-slate-900 truncate">{machine.name}</p>
                              <p className="text-xs text-slate-500 truncate">{machine.location || machine.venueType || '—'}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleToggleSelection({ machine, airport })}
                            className="ml-2 text-slate-400 hover:text-red-600 transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4">
                      <Button
                        variant="ghost"
                        onClick={handleClearSelection}
                        className="text-sm"
                      >
                        Clear All
                      </Button>
                      <Button
                        onClick={() => setViewMode('list')}
                        className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-6"
                      >
                        View in List
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sticky Action Bar - Bottom */}
      {selectedMachines.length > 0 && (
        <div className="fixed bottom-0 left-0 md:left-[248px] right-0 z-[2000] bg-white border-t border-slate-200 shadow-xl transition-all duration-300 ease-in-out">
          <div className="w-full px-4 py-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="inline-flex items-center justify-center rounded-full bg-blue-600 text-white text-lg font-bold px-4 py-1.5 min-w-[3rem]">
                  {selectedMachines.length}
                </div>
                <span className="text-base font-medium text-slate-900">
                  {selectionStats.venueCount > 0 ? `${selectionStats.venueCount} venue${selectionStats.venueCount > 1 ? 's' : ''} • ` : ''}{selectionStats.machineCount} machine{selectionStats.machineCount > 1 ? 's' : ''} selected
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  onClick={handleClearSelection}
                  className="text-slate-600 hover:text-slate-900 h-11 px-4"
                >
                  Clear Selection
                </Button>
                <Button
                  size="lg"
                  onClick={() => {
                    const hasTender = selectedMachines.some(m => m.machine.requiresTenderProcess);
                    const hasRegular = selectedMachines.some(m => !m.machine.requiresTenderProcess);
                    
                    if (hasTender && hasRegular) {
                      toast({
                        title: 'Mixed Selection Not Allowed',
                        description: 'You cannot mix regular machines and New Location machines in the same request. Please select only one type.',
                        variant: 'destructive',
                      });
                      return;
                    }
                    
                    setShowReservationModal(true);
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 h-11 text-base font-semibold"
                >
                  <Calendar className="h-5 w-5 mr-2" />
                  Request Reservation
                  <span className="ml-2 rounded-full bg-white/20 px-2.5 py-0.5 text-sm font-bold">
                    {selectedMachines.length}
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Machine Details Modal */}
      <Dialog open={!!selectedMachineForDetails} onOpenChange={(open) => {
        if (!open) {
          setSelectedMachineForDetails(null);
          setSkuAvailability(null);
        }
      }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col p-0" style={{ left: '55%', transform: 'translate(-50%, -50%)' }}>
          {selectedMachineForDetails && (() => {
            const { machine, airport } = selectedMachineForDetails;
            const areaImage = machine.MachineMedia?.find(m => m.kind === 'area');
            const renderImage = machine.MachineMedia?.find(m => m.kind === 'render');
            const hasBothImages = areaImage && renderImage;
            const singleImage = areaImage || renderImage || machine.MachineMedia?.[0];
            
            // Determine display status
            // For planogram machines, only show reserved if planogram is fully filled
            let displayStatus: string = machine.status || 'Live/Available';
            if (machine.reservationInfo?.userReservationStatus === 'pending') {
              displayStatus = 'Pending';
            } else if (machine.reservationInfo?.userReservationStatus === 'approved') {
              // For planogram machines, only show reserved if fully filled
              if (machine.requiresTenderProcess && machine.reservationInfo?.isPlanogramFullyReserved) {
                displayStatus = 'Reserved';
              } else if (!machine.requiresTenderProcess) {
              displayStatus = 'Reserved';
              }
              // If planogram machine but not fully filled, keep as Available
            }

            return (
              <>
                <DialogHeader className="px-6 pt-6 pb-4 border-b">
                  <DialogTitle className="text-2xl font-semibold">{machine.name}</DialogTitle>
                </DialogHeader>
                
                <div className="overflow-y-auto flex-1 px-6 py-4">
                  {/* Image Section */}
                  <div className="mb-6">
                    {hasBothImages ? (
                      <div className="relative">
                        <BeforeAfterSlider
                          beforeImage={areaImage.url}
                          afterImage={renderImage.url}
                          beforeLabel="Area"
                          afterLabel="Render"
                        />
                        <div className="absolute top-4 left-4 bg-blue-600 text-white text-sm font-semibold px-3 py-1 rounded">
                          {airport.code}
                        </div>
                      </div>
                    ) : singleImage ? (
                      <div className="relative w-full h-96 rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
                        <img
                          src={singleImage.url}
                          alt={machine.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23f1f5f9" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" font-family="Arial" font-size="14" fill="%2394a3b8" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                          }}
                        />
                        <div className="absolute top-4 left-4 bg-blue-600 text-white text-sm font-semibold px-3 py-1 rounded">
                          {airport.code}
                        </div>
                      </div>
                    ) : (
                      <div className="relative w-full h-96 rounded-lg bg-blue-100 border border-blue-200 flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-6xl font-semibold text-blue-700 mb-2">{airport.code}</div>
                          <p className="text-sm text-blue-600">No Image Available</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Details Section */}
                  <div className="space-y-6">
                    {/* Header Info */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        {renderStatusPill(displayStatus)}
                        {machine.availableSkus && machine.availableSkus > 0 && (
                          <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                            {machine.availableSkus} SKU{machine.availableSkus > 1 ? 's' : ''}
                          </span>
                        )}
                        {machine.requiresTenderProcess && (
                          <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold bg-purple-100 text-purple-700 border border-purple-200">
                            New Location
                          </span>
                        )}
                        {machine.label && (
                          <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 border border-blue-200">
                            {machine.label}
                          </span>
                        )}
                      </div>
                      
                      {machine.description && (
                        <p className="text-sm text-slate-600 leading-relaxed">
                          {machine.description}
                        </p>
                      )}
                      
                      {machine.costCents > 0 && (
                        <p className="text-base font-semibold text-slate-900">
                          Cost: {formatCurrency(machine.costCents)}
                        </p>
                      )}
                    </div>

                    {/* Technical Details Grid */}
                    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Status</p>
                        <p className="font-medium text-slate-900">{machine.status || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Available SKUs</p>
                        <p className="font-medium text-slate-900">
                          {machine.availableSkus !== null && machine.availableSkus !== undefined ? machine.availableSkus : '—'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Capacity</p>
                        <p className="font-medium text-slate-900">{machine.capacity || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Machine Type</p>
                        <p className="font-medium text-slate-900">{machine.machineType || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Resolution</p>
                        <p className="font-medium text-slate-900">{machine.resolution || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Format</p>
                        <p className="font-medium text-slate-900">{machine.format || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Screen Size</p>
                        <p className="font-medium text-slate-900">{machine.screenSize || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Playback Format</p>
                        <p className="font-medium text-slate-900">{machine.playbackFormat || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Duration</p>
                        <p className="font-medium text-slate-900">{machine.duration || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Orientation</p>
                        <p className="font-medium text-slate-900">{machine.orientation || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Aspect Ratio</p>
                        <p className="font-medium text-slate-900">{machine.aspectRatio || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Venue Type</p>
                        <p className="font-medium text-slate-900">{machine.venueType || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Venue Name</p>
                        <p className="font-medium text-slate-900">{machine.venueName || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Location</p>
                        <p className="font-medium text-slate-900">{machine.location || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Availability Type</p>
                        <p className="font-medium text-slate-900">{machine.availabilityType || '—'}</p>
                      </div>
                      {machine.requiresTenderProcess && machine.tenderTimeframe && (
                        <div>
                          <p className="text-xs uppercase text-slate-400 mb-1">Timeframe</p>
                          <p className="font-medium text-slate-900">{machine.tenderTimeframe}</p>
                        </div>
                      )}
                      {machine.requiresTenderProcess && machine.tenderLaunchDate && (
                        <div>
                          <p className="text-xs uppercase text-slate-400 mb-1">Launch Date</p>
                          <p className="font-medium text-slate-900">
                            {new Date(machine.tenderLaunchDate).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Planogram Configuration Section */}
                    {machine.requiresTenderProcess && machine.planogramConfig && (() => {
                      let planogramConfig: PlanogramConfig | null = null;
                      try {
                        planogramConfig = typeof machine.planogramConfig === 'string' 
                          ? JSON.parse(machine.planogramConfig)
                          : machine.planogramConfig;
                      } catch (e) {
                        console.error('Error parsing planogram config:', e);
                      }

                      if (!planogramConfig) return null;

                      const config = planogramConfig; // Store in const for TypeScript

                      const getCategoryForPosition = (row: number, col: number): string | undefined => {
                        return config.positions.find(p => p.row === row && p.col === col)?.category;
                      };

                      const getCategoryColor = (category: string | undefined): string => {
                        if (!category) return 'bg-gray-100';
                        const colors: Record<string, string> = {
                          'Energy Drinks': 'bg-yellow-100',
                          'Snacks': 'bg-orange-100',
                          'Beverages': 'bg-blue-100',
                          'Healthy Options': 'bg-green-100',
                          'Confectionery': 'bg-pink-100',
                        };
                        return colors[category] || 'bg-purple-100';
                      };

                      // Group positions by category for summary
                      const categoryCounts = new globalThis.Map<string, number>();
                      config.positions.forEach(pos => {
                        if (pos.category) {
                          categoryCounts.set(pos.category, (categoryCounts.get(pos.category) || 0) + 1);
                        }
                      });

                      return (
                        <div className="pt-4 border-t border-slate-200">
                          <div className="mb-4">
                            <p className="text-sm font-semibold text-slate-900 mb-2">Planogram Configuration</p>
                            <p className="text-xs text-slate-500 mb-4">
                              This machine has {config.positions.length} SKU positions configured across {categoryCounts.size} {categoryCounts.size === 1 ? 'category' : 'categories'}.
                            </p>
                            {categoryCounts.size > 0 && (
                              <div className="flex flex-wrap gap-2 mb-4">
                                {Array.from(categoryCounts.entries()).map(([category, count]) => (
                                  <span
                                    key={category}
                                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${getCategoryColor(category)} border border-slate-200`}
                                  >
                                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: getCategoryColor(category).replace('bg-', '').replace('-100', '-600') }}></span>
                                    {category}: {count} SKU{count !== 1 ? 's' : ''}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="border rounded-lg p-4 bg-gray-50 overflow-x-auto">
                            <div className="min-w-[600px]">
                              {Array.from({ length: config.rows }, (_, row) => (
                                <div key={row} className="flex gap-2 mb-2">
                                  {Array.from({ length: config.columns }, (_, col) => {
                                    const category = getCategoryForPosition(row, col);
                                    const bgColor = getCategoryColor(category);
                                    
                                    // Check if this position is allocated
                                    const allocation = skuAvailability?.allocations?.find(
                                      alloc => alloc.rowIndex === row && alloc.columnIndex === col
                                    );
                                    const isPending = allocation?.status === 'pending';
                                    const isApproved = allocation?.status === 'approved';
                                    
                                    return (
                                      <div
                                        key={col}
                                        className={`
                                          w-16 h-16 rounded border-2 text-xs font-medium flex flex-col items-center justify-center relative
                                          ${isApproved ? 'bg-gray-300 opacity-60' : bgColor}
                                          ${isPending ? 'border-yellow-500 border-3 shadow-md' : 'border-gray-300'}
                                          ${isApproved ? 'border-gray-400' : ''}
                                        `}
                                        title={isPending ? 'Pending Request' : isApproved ? 'Approved' : 'Available'}
                                      >
                                        <div className="text-[10px] text-slate-600">{row},{col}</div>
                                        {category && (
                                          <div className="text-[9px] mt-1 text-slate-700 font-medium px-1 text-center leading-tight">
                                            {category.substring(0, 8)}
                                          </div>
                                        )}
                                        {isPending && (
                                          <div className="absolute top-0 right-0 w-3 h-3 bg-yellow-500 rounded-full border border-white"></div>
                                        )}
                                        {isApproved && (
                                          <div className="absolute top-0 right-0 w-3 h-3 bg-gray-500 rounded-full border border-white"></div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              ))}
                            </div>
                            {(skuAvailability?.allocations?.some(a => a.status === 'pending') || skuAvailability?.allocations?.some(a => a.status === 'approved')) && (
                              <div className="mt-4 flex items-center gap-4 text-xs text-slate-600">
                                {skuAvailability?.allocations?.some(a => a.status === 'pending') && (
                                  <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-yellow-500 rounded-full border border-white"></div>
                                    <span>Pending Request</span>
                                  </div>
                                )}
                                {skuAvailability?.allocations?.some(a => a.status === 'approved') && (
                                  <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-gray-500 rounded-full border border-white"></div>
                                    <span>Approved</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Address Section */}
                    {(machine.addressLine1 || machine.city || machine.postCode) && (
                      <div className="pt-4 border-t border-slate-200">
                        <p className="text-xs uppercase text-slate-400 mb-2">Address</p>
                        <p className="text-sm font-medium text-slate-900">
                          {[machine.addressLine1, machine.addressLine2, machine.city, machine.county, machine.postCode]
                            .filter(Boolean)
                            .join(', ')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Reservation Request Modal */}
      {showReservationModal && (
        <>
          {selectedMachines.some(m => m.machine.requiresTenderProcess) ? (
            <TenderRequestModal
              selectedMachines={selectedMachines}
              onClose={() => setShowReservationModal(false)}
              onSuccess={() => {
                setSelectedMachines([]);
                loadData();
              }}
            />
          ) : (
            <ReservationRequestModal
              selectedMachines={selectedMachines.map(sm => sm.machine)}
              onClose={() => setShowReservationModal(false)}
              onSuccess={() => {
                setSelectedMachines([]);
                loadData();
              }}
            />
          )}
        </>
      )}
    </div>
  );
}
